/**
 * Pure helpers for the OsLogo component: family normalization, distro detection,
 * caption composition, and Windows marketing-name derivation.
 *
 * Kept in a separate file so the OsLogo.tsx module only exports React components
 * and fast refresh works correctly in dev.
 */

/** Canonical family we render brand artwork for. */
export type OsFamily = 'windows' | 'macos' | 'linux';

/**
 * resolveOsFamily normalizes the agent-reported `os` string to one of the
 * three families we have branded artwork for. Returns `null` for unknown /
 * empty inputs (callers should render a generic fallback).
 */
export function resolveOsFamily(os: string | undefined): OsFamily | null {
  if (!os) return null;
  const s = os.toLowerCase();
  if (s === 'darwin' || s === 'mac' || s === 'macos' || s.includes('os x')) {
    return 'macos';
  }
  if (s === 'windows' || s.includes('windows')) {
    return 'windows';
  }
  if (s === 'linux' || s.includes('linux') || s.includes('ubuntu') || s.includes('debian')) {
    return 'linux';
  }
  return null;
}

/** Map a family to the file key under /public/os-logos/. */
export function logoFileKey(family: OsFamily): string {
  return family; // keys mirror the family name
}

/** Map a Linux osVersion string to a distro logo key + caption. */
export function resolveLinuxDistro(osVersion: string | undefined): {
  key: string;
  name: string;
} {
  const v = (osVersion || '').toLowerCase();
  if (v.startsWith('ubuntu')) return { key: 'ubuntu', name: 'Ubuntu' };
  if (v.startsWith('debian')) return { key: 'debian', name: 'Debian' };
  if (v.startsWith('fedora')) return { key: 'fedora', name: 'Fedora' };
  if (v.startsWith('red hat')) return { key: 'redhat', name: 'Red Hat' };
  if (v.startsWith('centos')) return { key: 'centos', name: 'CentOS' };
  if (v.startsWith('arch')) return { key: 'arch', name: 'Arch' };
  if (v.startsWith('opensuse') || v.startsWith('suse'))
    return { key: 'opensuse', name: 'openSUSE' };
  return { key: 'linux', name: 'Linux' };
}

/**
 * Derive a Windows "marketing" name from the raw build string we receive
 * (e.g. "10.0.22631" → "Windows 11"). Windows 11 is build ≥ 22000.
 * Returns "" when the build string is missing or unparseable.
 */
export function windowsMarketingName(osVersion: string | undefined): string {
  if (!osVersion) return '';
  const match = /^(\d+)\.(\d+)\.(\d+)/.exec(osVersion);
  if (!match) return '';
  const build = Number(match[3]);
  if (build >= 22000) return 'Windows 11';
  if (match[1] === '10') return 'Windows 10';
  if (match[1] === '6' && match[2] === '3') return 'Windows 8.1';
  if (match[1] === '6' && match[2] === '2') return 'Windows 8';
  if (match[1] === '6' && match[2] === '1') return 'Windows 7';
  return `Windows ${match[1]}.${match[2]}`;
}

/**
 * Compute the caption shown under the logo.
 * Examples:
 *   ("windows", "10.0.22631")           → "Windows 11"
 *   ("windows", "10.0.19045")           → "Windows 10"
 *   ("darwin",  "14.5")                 → "macOS 14.5"
 *   ("linux",   "Ubuntu 24.04 LTS")     → "Ubuntu 24.04 LTS"
 *   ("linux",   undefined)              → "Linux"
 */
export function resolveCaption(family: OsFamily, osVersion: string | undefined): string {
  if (!osVersion) return family === 'macos' ? 'macOS' : family === 'windows' ? 'Windows' : 'Linux';
  if (family === 'windows') {
    const name = windowsMarketingName(osVersion);
    return name || 'Windows';
  }
  if (family === 'macos') {
    return `macOS ${osVersion}`;
  }
  // Linux: keep the PRETTY_NAME verbatim when available.
  return osVersion;
}
